import { useFloating, offset, flip, shift } from '@floating-ui/react';
import { Popover } from '@headlessui/react';
import { Pill } from '@iwoca/orion';
import clsx from 'clsx';
import ReactDOM from 'react-dom';
import styles from './NumberPicker.module.css';
import { Stepper } from './Stepper/Stepper';

type Toption = {
  value: number;
  allowed: boolean;
};

type TProps = {
  options: Toption[];
  value: number | null;
  id?: string;
  title?: string;
  pills?: string[];
  subTitle?: string;
  disabled?: boolean;
  disabledStepper?: boolean;
  onChange: (value: number) => void;
};

export const NumberPicker = ({
  options,
  value,
  id,
  title,
  pills,
  subTitle,
  disabled,
  disabledStepper,
  onChange,
}: TProps) => {
  const { refs, floatingStyles } = useFloating({
    placement: 'top',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });

  const selectedIndex =
    value === null ? 0 : options.findIndex((option) => option.value === value);
  const index = Math.max(0, selectedIndex);

  const lastAllowedIndex = options.reduce(
    (lastIndex, option, i) => (option.allowed ? i : lastIndex),
    -1,
  );

  const dataTestId = `number-picker${id ? `-${id}` : ''}`;

  return (
    <div className={styles.numberPicker} data-testid={dataTestId}>
      <div className={styles.popoverAndStepper}>
        <Popover className={styles.popover}>
          {({ close, open }) => (
            <>
              <Popover.Button
                data-testid="number-picker-button"
                disabled={disabled}
                className={clsx(styles.popoverButton, {
                  [styles.popoverOpen]: open,
                })}
                ref={refs.setReference}
              >
                {value !== null ? `${value}%` : '—'}
              </Popover.Button>
              {ReactDOM.createPortal(
                <Popover.Panel
                  className={styles.popoverPanel}
                  ref={refs.setFloating}
                  style={floatingStyles}
                >
                  {(title || (pills && pills?.length > 0)) && (
                    <div>
                      {title && (
                        <h3 className={styles.panelHeading}>{title}</h3>
                      )}
                      {pills && pills?.length > 0 && (
                        <div className={styles.pillContainer}>
                          {pills.map((label) => (
                            <Pill key={label}>{label}</Pill>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {subTitle && <p className={styles.disclaimer}>{subTitle}</p>}
                  <div className={styles.grid}>
                    {options.map((option, i) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          if (!option.allowed) return;
                          onChange(option.value);
                          close();
                        }}
                        className={clsx(styles.cell, {
                          [styles.selected]: i === index,
                          [styles.disabled]: !option.allowed,
                        })}
                        role="option"
                        aria-selected={i === index}
                      >
                        {option.value.toFixed(1)}
                      </button>
                    ))}
                  </div>
                </Popover.Panel>,
                document.body,
              )}
            </>
          )}
        </Popover>
        <Stepper
          index={index}
          minIndex={0}
          disabled={disabledStepper}
          maxIndex={lastAllowedIndex}
          onChange={(newIndex) => onChange(options[newIndex].value)}
        />
      </div>
    </div>
  );
};
